import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  Users,
  Briefcase,
  CalendarDays,
  ClipboardList,
  Network,
  UserPlus,
  Target,
  FileText,
} from 'lucide-react';
import { Bar, BarChart, CartesianGrid, Cell, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { HrPageShell } from './components/HrPageShell';
import { EmployeeAvatar, Panel } from './components/hr-ui';
import { useEmployees, useTimeOffRequests, useReviewCycles, useOnboardingTasks } from './hr-store';
import { useJobsQuery } from '@/client/api/hr/records';
import { hasLeft, EMPLOYMENT_TYPE_LABEL } from '@/client/types/hr';

const DEPARTMENT_COLORS = ['#2563eb', '#7c3aed', '#059669', '#d97706', '#e11d48', '#0891b2'];

const OverviewPage = () => {
  const employees = useEmployees();
  const jobPostings = useJobsQuery().data ?? [];
  const timeOffRequests = useTimeOffRequests();
  const reviewCycles = useReviewCycles();
  const onboardingTasks = useOnboardingTasks();

  const activeEmployees = useMemo(() => employees.filter((e) => !hasLeft(e)), [employees]);
  const pendingRequests = useMemo(() => timeOffRequests.filter((r) => r.status === 'pending'), [timeOffRequests]);
  const openPostings = useMemo(() => jobPostings.filter((j) => j.status === 'open'), [jobPostings]);
  const activeCycle = useMemo(() => reviewCycles.find((c) => c.status === 'active'), [reviewCycles]);
  const openOnboarding = useMemo(
    () => onboardingTasks.filter((t) => t.kind === 'onboarding' && t.status !== 'completed'),
    [onboardingTasks],
  );

  const byDepartment = useMemo(() => {
    const counts = new Map<string, number>();
    for (const e of activeEmployees) counts.set(e.department, (counts.get(e.department) ?? 0) + 1);
    return [...counts.entries()].map(([department, count]) => ({ department, count })).sort((a, b) => b.count - a.count);
  }, [activeEmployees]);

  const byEmploymentType = useMemo(() => {
    const counts = new Map<string, number>();
    for (const e of activeEmployees) counts.set(e.employmentType, (counts.get(e.employmentType) ?? 0) + 1);
    return [...counts.entries()].map(([type, value], i) => ({
      name: EMPLOYMENT_TYPE_LABEL[type as keyof typeof EMPLOYMENT_TYPE_LABEL],
      value,
      fill: DEPARTMENT_COLORS[i % DEPARTMENT_COLORS.length],
    }));
  }, [activeEmployees]);

  const recentHires = useMemo(
    () => [...activeEmployees].sort((a, b) => new Date(b.startDate).getTime() - new Date(a.startDate).getTime()).slice(0, 5),
    [activeEmployees],
  );

  return (
    <HrPageShell
      title="Human Resource overview"
      description="Headcount, hiring, and team health at a glance."
      crumbs={[{ label: 'Human Resource' }]}
      icon={Users}
      stats={[
        { key: 'headcount', label: 'Active headcount', value: activeEmployees.length },
        { key: 'openings', label: 'Open positions', value: openPostings.reduce((s, j) => s + j.openings, 0) },
        { key: 'pending_to', label: 'Pending time off', value: pendingRequests.length },
        { key: 'onboarding', label: 'Open onboarding tasks', value: openOnboarding.length },
      ]}
    >
      <div className="mb-6 grid gap-4 lg:grid-cols-3">
        <Panel title="Headcount by department" className="lg:col-span-2">
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={byDepartment} layout="vertical" margin={{ left: 16 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11 }} />
                <YAxis type="category" dataKey="department" width={100} tick={{ fontSize: 11 }} />
                <Tooltip />
                <Bar dataKey="count" radius={[0, 4, 4, 0]} name="Employees" fill="#0f172a" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Panel>

        <Panel title="Employment type">
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={byEmploymentType} dataKey="value" nameKey="name" innerRadius={48} outerRadius={78} paddingAngle={2}>
                  {byEmploymentType.map((d) => (
                    <Cell key={d.name} fill={d.fill} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </Panel>
      </div>

      <div className="mb-6 grid gap-4 lg:grid-cols-2">
        <Panel
          title="Recent hires"
          action={
            <Link to="/admin/human-resource/people/directory" className="text-xs font-medium text-blue-700 hover:underline">
              Directory →
            </Link>
          }
        >
          {recentHires.length === 0 ? (
            <p className="py-8 text-center text-sm text-gray-500">No employees yet.</p>
          ) : (
            <ul className="space-y-2">
              {recentHires.map((e) => (
                <li key={e.id}>
                  <Link
                    to={`/admin/human-resource/people/directory/${e.id}`}
                    className="flex items-center gap-2.5 rounded-lg px-2 py-1.5 hover:bg-gray-50"
                  >
                    <EmployeeAvatar employee={e} size="sm" />
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-gray-900">{e.name}</p>
                      <p className="truncate text-xs text-gray-400">{e.title}</p>
                    </div>
                    <span className="shrink-0 text-xs text-gray-400">{new Date(e.startDate).toLocaleDateString()}</span>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </Panel>

        <Panel
          title="Active review cycle"
          action={
            <Link to="/admin/human-resource/performance/cycles" className="text-xs font-medium text-blue-700 hover:underline">
              Review cycles →
            </Link>
          }
        >
          {activeCycle ? (
            <div className="rounded-lg border border-emerald-100 bg-emerald-50 p-3">
              <p className="font-medium text-emerald-900">{activeCycle.name}</p>
              <p className="mt-1 text-xs text-emerald-800">
                {new Date(activeCycle.startDate).toLocaleDateString()} – {new Date(activeCycle.endDate).toLocaleDateString()}
              </p>
            </div>
          ) : (
            <p className="py-8 text-center text-sm text-gray-500">No active review cycle right now.</p>
          )}
        </Panel>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {[
          { label: 'Org chart', href: '/admin/human-resource/people/org-chart', icon: Network },
          { label: 'Job postings', href: '/admin/human-resource/recruiting/postings', icon: Briefcase },
          { label: 'Time off requests', href: '/admin/human-resource/time-off/requests', icon: CalendarDays },
          { label: 'Onboarding', href: '/admin/human-resource/onboarding', icon: UserPlus },
          { label: 'Goals', href: '/admin/human-resource/performance/goals', icon: Target },
          { label: 'Review cycles', href: '/admin/human-resource/performance/cycles', icon: ClipboardList },
          { label: 'Policies', href: '/admin/human-resource/documents/policies', icon: FileText },
          { label: 'Directory', href: '/admin/human-resource/people/directory', icon: Users },
        ].map((link) => (
          <Link
            key={link.href}
            to={link.href}
            className="flex items-center gap-3 rounded-xl border border-gray-100 bg-white px-4 py-3 text-sm font-medium text-gray-800 shadow-sm transition hover:border-blue-200 hover:bg-blue-50"
          >
            <link.icon className="h-4 w-4 text-blue-700" />
            {link.label} →
          </Link>
        ))}
      </div>
    </HrPageShell>
  );
};

export default OverviewPage;
